import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'react-toastify';
import MainLayout from '../../components/Layout/MainLayout';
import chairmanReportsService from '../../services/chairmanReportsService';
import applicationService from '../../services/applicationService';

const DepartureReportPage = () => {
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    countryId: '',
    jobRoleId: '',
    page: 1,
    limit: 10
  });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({
    departedCount: 0,
    noShowCount: 0,
    countryBreakdown: [],
    candidates: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 1 }
  });
  const [filterOptions, setFilterOptions] = useState({ countries: [], jobRoles: [] });

  const fetchFilterOptions = async () => {
    try {
      const response = await applicationService.getFilterOptions();
      if (response?.success) {
        setFilterOptions({
          countries: response.data?.countries || [],
          jobRoles: response.data?.jobRoles || []
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load filter options');
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await chairmanReportsService.getDepartureReport(filters);
      if (response?.success) {
        setReport(response.data || {});
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load departure report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [filters.fromDate, filters.toDate, filters.countryId, filters.jobRoleId, filters.page, filters.limit]);

  const uniqueCountries = useMemo(() => report.countryBreakdown?.length || 0, [report.countryBreakdown]);

  const countryChartData = useMemo(() => {
    return (report.countryBreakdown || []).map((item) => ({
      country: item.countryName,
      departed: item.count
    }));
  }, [report.countryBreakdown]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h1 className="text-3xl font-bold text-charcoal">Departure Report</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Total Departed" value={report.departedCount || 0} />
          <StatCard label="Countries Covered" value={uniqueCountries} />
          <StatCard label="Visible Rows" value={report.candidates?.length || 0} />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-1 md:grid-cols-7 gap-3">
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => setFilter('fromDate', e.target.value)}
            className="border rounded-lg px-3 py-2"
          />
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => setFilter('toDate', e.target.value)}
            className="border rounded-lg px-3 py-2"
          />
          <select
            value={filters.countryId}
            onChange={(e) => setFilter('countryId', e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">All Countries</option>
            {filterOptions.countries.map((country) => (
              <option key={country.value || country._id || country.name} value={country.value || country._id || country.name}>
                {country.label || country.name}
              </option>
            ))}
          </select>
          <select
            value={filters.jobRoleId}
            onChange={(e) => setFilter('jobRoleId', e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">All Job Roles</option>
            {filterOptions.jobRoles.map((role) => (
              <option key={role.value || role._id || role.title} value={role.value || role._id || role.title}>
                {role.label || role.title}
              </option>
            ))}
          </select>
          <select
            value={filters.limit}
            onChange={(e) => setFilter('limit', Number(e.target.value))}
            className="border rounded-lg px-3 py-2"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </select>
          <button
            onClick={() => setFilters({ fromDate: '', toDate: '', countryId: '', jobRoleId: '', page: 1, limit: filters.limit })}
            className="px-4 py-2 bg-gray-100 rounded-lg"
          >
            Clear
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-semibold text-charcoal mb-4">Departure by Country</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={countryChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="country" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="departed" fill="#5B8C5A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>Candidate</Th>
                <Th>Passport</Th>
                <Th>Country</Th>
                <Th>Departed Date</Th>
                <Th>Job Role</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center">Loading...</td>
                </tr>
              ) : (report.candidates || []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center">No departed candidates found</td>
                </tr>
              ) : (
                (report.candidates || []).map((row) => (
                  <tr key={row._id} className="border-t">
                    <Td>{row.candidateName || '-'}</Td>
                    <Td>{row.passportNumber || '-'}</Td>
                    <Td>{row.destinationCountry || '-'}</Td>
                    <Td>{row.departedAt ? new Date(row.departedAt).toLocaleString() : '-'}</Td>
                    <Td>{row.jobRole || '-'}</Td>
                    <Td>{row.email || '-'}</Td>
                    <Td>{row.phone || '-'}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          pagination={report.pagination}
          onPageChange={(nextPage) => setFilters((prev) => ({ ...prev, page: nextPage }))}
        />
      </div>
    </MainLayout>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-2xl mt-2 font-semibold text-charcoal">{value}</p>
  </div>
);

const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs uppercase text-gray-600">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;

const Pagination = ({ pagination, onPageChange }) => (
  <div className="flex items-center justify-between">
    <p className="text-sm text-gray-600">
      Page {pagination?.page || 1} of {pagination?.totalPages || 1}
    </p>
    <div className="flex gap-2">
      <button
        className="px-3 py-1 border rounded disabled:opacity-50"
        disabled={!pagination?.page || pagination.page <= 1}
        onClick={() => onPageChange(pagination.page - 1)}
      >
        Previous
      </button>
      <button
        className="px-3 py-1 border rounded disabled:opacity-50"
        disabled={!pagination?.page || pagination.page >= pagination.totalPages}
        onClick={() => onPageChange(pagination.page + 1)}
      >
        Next
      </button>
    </div>
  </div>
);

export default DepartureReportPage;
